import { useState, useLayoutEffect } from 'react';
import {
  useTranslation,
  FAIcon,
  IconStyle,
  Theme,
  useNextgenRouter,
  Button,
  ButtonStyles,
  ButtonSize,
  ButtonWidth,
  useSession,
  useBreakpoint,
  NextgenImage,
  LoaderType,
} from '@nextgen-web-framework/all';
import { useSelector, useDispatch } from 'react-redux';
import type { AppState, AppDispatch } from '@nextgen-web-framework/core';
import { cmsApi } from '@commerce-webcomponents/sdk';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import {
  ProgressiveProfileNextStepEnum,
  CMS_CONTENT_TYPE,
  ProgressiveProfileCMSDataProps,
  ProgressiveProfileResult,
  getProgressiveProfileCmsDataTransformer,
  TaskStatus,
  getProfileStepperImageCmsDataTransformer,
  ProfileStepperCMSDataProps,
  ImageReturnType,
} from '@commerce-profile-webcomponents/sdk';
import { getTaskListUtil, isMobileView } from '../../utils/profileUtils';
import { CONSTANTS } from '../../utils/Constants';
import { useGlobalErrorHandler } from '../../utils/globalErrorHook';
import type { ProgressiveProfileProps, ProgressiveProfileWrapperProps } from './types/propTypes';
import styles from './ProgressiveProfile.module.scss';

const ProgressiveProfileWrapper = ({ children, images }: ProgressiveProfileWrapperProps) => {
  const viewPortType = useBreakpoint();
  if (images && images.length > 0 && !isMobileView(viewPortType)) {
    const transformedImages: ProfileStepperCMSDataProps = getProfileStepperImageCmsDataTransformer(images);
    const image = transformedImages?.data?.find((item: ImageReturnType) => item?.viewport.includes(viewPortType));
    return (
      <div
        className={`${styles['profile-progressive-profile-image-wrapper']}`}
        data-testid="profile-progressive-profile-image-wrapper"
      >
        <div className={`${styles['progressive-profile-child-wrapper']}`}>{children}</div>
        {image && (
          <div className={`${styles['profile-progressive-profile-image']}`}>
            <NextgenImage src={image?.url} loaderType={LoaderType.CONTENTSTACK} alt={image?.altText} />
          </div>
        )}
      </div>
    );
  }
  return <>{children}</>;
};

const ProgressiveProfile = ({
  subtitleText,
  showHeading = false,
  nextStepType,
  showSkipButton = false,
  homepageImage,
}: ProgressiveProfileProps) => {
  const { session } = useSession();
  const { aboNum, type } = session;
  const dispatch = useDispatch<AppDispatch>();
  const { taskCompleted, tasks } = useSelector((state: AppState) => state.commerceProfile.tasksInformation);
  const [iconData, setIconData] = useState([]);
  const [showDate, setShowDate] = useState(false);
  const [daysCount, setDaysCount] = useState(0);
  // eslint-disable-next-line
  const [daysAndUrlMapper, setDaysAndUrlMapper] = useState<any>({
    days: [],
    urls: [],
  });
  const apiErrorHandler = useGlobalErrorHandler();
  let hasSameCompletionDate = false;
  const { urls, days } = daysAndUrlMapper;

  const getData = () => {
    if (tasks.length !== 0) {
      cmsApi
        .fetchContent({
          params: [{ key: 'query', value: JSON.stringify({ user_account: { account_type: type } }) }],
          uId: CMS_CONTENT_TYPE.PROGRESSIVE_PROFILE_STEPPER,
        })
        .then((cmsResponse) => {
          const { data }: ProgressiveProfileCMSDataProps = getProgressiveProfileCmsDataTransformer(
            cmsResponse as ProgressiveProfileResult,
          );
          const { icons } = data;
          const iconList = Object.keys(icons).map((icon) => {
            const cmsData = icons[icon as keyof typeof icons];
            // eslint-disable-next-line
            const taskData = tasks.find((t: any) => t.type.toLocaleLowerCase() === icon);
            return { taskName: icon, ...cmsData, ...taskData };
          });

          const filteredIconList = iconList.filter((list) => list.iconName?.length !== undefined);
          setIconData(filteredIconList as []);
        });
    }
  };
  // called at the first render to check if any task is in incomplete status
  useLayoutEffect(() => {
    // if tasks already exist in slice then CMS API will be called directly
    if (tasks.length === 0) {
      const accountNumber = aboNum;
      getTaskListUtil(dispatch, accountNumber as string, apiErrorHandler);
    }
  }, []);

  //This will be called only once, when taskCompleted change i.e at initial render
  useLayoutEffect(() => {
    if (!taskCompleted) {
      getData();
    }
  }, [tasks]);
  // This function will be called when the data from cms and redux is available
  useLayoutEffect(() => {
    const { days, urls } = daysAndUrlMapper;
    const newDays: number[] = [];
    const newUrls: string[] = [];

    iconData.forEach(({ status, url, dueDate }) => {
      if (dueDate) {
        const day = getRemainingDays(dueDate);
        newDays.push(day);
      }
      if (status === TaskStatus.Incomplete) {
        newUrls.push(url);
      }
    });
    const updatedDays = [...days, ...newDays];
    const updatedUrls = [...urls, ...newUrls];

    hasSameCompletionDate = updatedDays.length != 0 && new Set(updatedDays).size !== updatedDays.length;
    hasSameCompletionDate ? setDaysCount(updatedDays[0]) : setDaysCount(0);
    setShowDate(!hasSameCompletionDate && updatedDays.length != 0);
    setDaysAndUrlMapper({ ...daysAndUrlMapper, days: updatedDays, urls: updatedUrls });
  }, [iconData]);

  const getRemainingDays = (date: string) => {
    const today = new Date();
    const due = new Date(date);
    return Math.round((due?.getTime() - today.getTime()) / CONSTANTS.TIME_COVERSION);
  };
  const showDaysWithDate = (date: string) =>
    t(getRemainingDays(date) === 1 ? 'profile.progressiveStepper.day' : 'profile.progressiveStepper.days');
  const showDays = (days: number) =>
    t(days === 1 ? 'profile.progressiveStepper.day' : 'profile.progressiveStepper.days');
  const { BUTTON, LINK } = ProgressiveProfileNextStepEnum;
  const t = useTranslation();
  const showNextStepTextlink = nextStepType === LINK;
  const showNextStepButton = nextStepType === BUTTON;
  const { push } = useNextgenRouter();
  const viewPortType = useBreakpoint();
  const proceedToNextStepCtaLabel = showSkipButton
    ? 'profile.progressiveStepper.nextStepText'
    : 'profile.progressiveStepper.proceedText';
  const handlerRedirectUrl = (url: string) => {
    push?.(url);
  };
  const addSubHeadingCenter = showNextStepButton || (!nextStepType && !isMobileView(viewPortType));
  const addSubHeadingPadding = showNextStepButton && !showSkipButton;
  const showIcons = (
    iconName: IconName,
    status: string,
    url: string,
    index: number,
    dueDate: string,
    nextStep: string,
  ) => {
    const isStepCompleted = status !== TaskStatus.Incomplete;
    return (
      <div className={`${styles['progressive-profile-container']}`} data-testid="progressive-profile-container">
        <div
          className={styles['progressive-profile-stepper-container']}
          data-testid="progressive-profile-stepper-container"
        >
          <div className={styles['progressive-profile-icon-wrapper']}>
            {isStepCompleted && (
              <div className={styles['progressive-profile-complete-icon']}>
                <FAIcon
                  iconName="circle-check"
                  iconStyle={IconStyle.SOLID}
                  className={styles['profile-complete-icon']}
                />
              </div>
            )}
            <div className={styles['progressive-profile-icon-border']}>
              <div
                className={`${styles['progressive-profile-icon']} ${isStepCompleted ? styles['step-completed'] : ''}`}
              >
                <FAIcon
                  iconName={iconName}
                  iconStyle={IconStyle.REGULAR}
                  theme={Theme.WHITE}
                  className={styles.icon}
                  data-testId="profile-icon-test-id"
                  onClick={() => {
                    if (isStepCompleted) {
                      return;
                    }
                    handlerRedirectUrl(url);
                  }}
                />
              </div>
            </div>
          </div>
          {index + 1 < iconData.length && <div className={styles['progressive-profile-icon-divider']}></div>}
        </div>
        {showDate ? (
          <div
            className={`${styles['progressive-profile-days-status']} ${
              nextStep === iconName ? styles['progressive-profile-day-status-bold'] : ''
            }`}
          >
            {isStepCompleted
              ? t('profile.progressiveStepper.done')
              : `${getRemainingDays(dueDate)} ${showDaysWithDate(dueDate)}`}
          </div>
        ) : null}
      </div>
    );
  };
  function createSubtitleText() {
    return {
      __html: t(`${subtitleText}`, {
        days: daysCount != 0 ? `<b>${daysCount} ${showDays(days[0])}</b>` : '',
        daysRemaining: daysCount === 0 ? 'days remaining' : '',
      }),
    };
  }

  return (
    <>
      {!taskCompleted && (
        <ProgressiveProfileWrapper images={homepageImage}>
          <div
            className={`${styles['profile-progressive-profile-container']}`}
            data-testid="profile-progressive-profile-container"
          >
            <div
              className={`${styles['progressive-profile-wrapper']} ${
                !nextStepType ? styles['progressive-profile-border'] : ''
              } ${showNextStepTextlink ? styles['progressive-profile-wrapper-me-panel'] : ''}`}
              data-testid="progressive-profile-wrapper"
            >
              {showHeading && (
                <div
                  className={`${styles['progressive-profile-heading']} ${
                    showNextStepTextlink ? styles['progressive-profile-heading-me-panel'] : ''
                  }`}
                  data-testid="progressive-profile-heading"
                >
                  {t('profile.progressiveStepper.heading')}
                </div>
              )}
              {subtitleText && (
                <div
                  className={`${styles['progressive-profile-subheading']} ${
                    addSubHeadingCenter ? styles['progressive-profile-subheading-center'] : ''
                  }
            ${addSubHeadingPadding ? styles['subtitle-padding'] : ''}`}
                  data-testid="progressive-profile-subheading"
                  dangerouslySetInnerHTML={createSubtitleText()}
                ></div>
              )}
              <div
                className={`${styles['progressive-profile-stepper-wrapper']}  ${
                  showNextStepTextlink ? styles['progressive-profile-stepper-wrapper-me-panel'] : ''
                }`}
                data-testid="progressive-profile-stepper-wrapper"
              >
                {iconData.map((icon, index) => {
                  const { iconName, status, url, dueDate } = icon;
                  // eslint-disable-next-line
                  const nextStep: any = iconData?.find(({ status }) => status === TaskStatus.Incomplete);

                  return showIcons(iconName as IconName, status, url, index, dueDate, nextStep?.iconName);
                })}
              </div>
              {showNextStepButton && (
                <div className={`${styles['button-wrapper']}`}>
                  <div className={styles['proceed-button']}>
                    <Button
                      data-testId="proceed-button-id"
                      title={t(proceedToNextStepCtaLabel)}
                      buttonStyle={ButtonStyles.SOLID}
                      size={ButtonSize.SMALL}
                      theme={Theme.GREEN}
                      buttonWidth={ButtonWidth.FULL_WIDTH}
                      onClick={() => handlerRedirectUrl(urls[0])}
                    />
                  </div>

                  {showSkipButton && (
                    <div className={styles['skip-button']}>
                      <Button
                        data-testId="skip-button-id"
                        title={t('profile.progressiveStepper.skipText')}
                        buttonStyle={ButtonStyles.OUTLINE}
                        size={ButtonSize.SMALL}
                        theme={Theme.GREEN}
                        buttonWidth={ButtonWidth.FULL_WIDTH}
                        onClick={() => handlerRedirectUrl('/')}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            {showNextStepTextlink && (
              <div
                className={styles['progressive-profile-stepper-textlink']}
                onClick={() => handlerRedirectUrl(urls[0])}
                tabIndex={-1}
                aria-hidden="true"
                data-testId="proceed-text-link"
              >
                <span>{t(proceedToNextStepCtaLabel)}</span>
                <FAIcon iconName="chevron-right" iconStyle={IconStyle.SOLID} theme={Theme.GREEN} />
              </div>
            )}
          </div>
        </ProgressiveProfileWrapper>
      )}
    </>
  );
};
export default ProgressiveProfile;
